import { call, select, put, takeEvery } from 'redux-saga/effects';
import { ReservationsResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import { fetchEntity } from '@classpass/saga-tools';

import LINKS from 'constants/links';
import URLS from 'constants/urls';
import {
  OPTIMIZELY_FEATURE_FLAGS_KEYS,
  PARTNER_DASHBOARD_SERVICE_PRICING_PROOF,
} from 'components/Optimizely/Optimizely.features';
import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';
import { openNewTab } from 'helpers/page-utils';
import { reportError } from 'helpers/error-logging';
import { CLASS_ENTITY_TYPE, VENUE_SERVICE_TYPES, PERMISSION } from 'constants/logic';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { waitForModal } from 'components/Modal';
import { selectActiveVenue, selectUserHasPermission, selectUserId } from 'selectors/general';
import { venueClasses } from 'store/saga/entities/bound';
import { fetchRetailRateRequestsByVenue, mergeRetailRateRequestsByClassId } from '../retailRateRequests.helpers';
import messages from './ClassList.messages';

export function* selectIsServicePricingProofEnabledInSaga() {
  const userId = yield select(selectUserId);

  if (!userId) {
    return false;
  }

  try {
    return yield call(
      isFeatureEnabledForUser,
      OPTIMIZELY_FEATURE_FLAGS_KEYS[PARTNER_DASHBOARD_SERVICE_PRICING_PROOF],
      userId
    );
  } catch (error) {
    yield call(reportError, 'Failed to resolve service-pricing-proof flag', { error, userId });
    return false;
  }
}

export function* fetchVenueClassesWithRetailRateRequests(client, fetchParams) {
  const { venueId } = fetchParams;
  const classes = yield call(fetchEntity, venueClasses, [client, fetchParams]);
  const isPricingProofEnabled = yield call(selectIsServicePricingProofEnabledInSaga);

  if (!isPricingProofEnabled) {
    return classes;
  }

  try {
    const requests = yield call(fetchRetailRateRequestsByVenue, venueId);
    const mergedClasses = mergeRetailRateRequestsByClassId(classes, requests);

    yield put(venueClasses.successAction({ data: mergedClasses, key: venueId }));

    return mergedClasses;
  } catch (error) {
    yield call(reportError, 'Failed to fetch venue retail rate requests for integrated list', { error, venueId });
    return classes;
  }
}

export const UPDATE_IS_SERVICE_ENABLED = 'UPDATE_IS_SERVICE_ENABLED';
export const updateIsServiceEnabled = (venueClass, isEnabled) => ({
  type: UPDATE_IS_SERVICE_ENABLED,
  venueClass,
  isEnabled,
});

// The classes list is otherwise only fetched on route enter, and the detail
// and list views share one route — navigating back after a pricing submit
// re-renders stale data until something re-fetches it.
export const REFRESH_VENUE_CLASSES = 'REFRESH_VENUE_CLASSES';
export const refreshVenueClasses = () => ({ type: REFRESH_VENUE_CLASSES });

export function* doUpdateIsServiceEnabled(dependencies, action) {
  try {
    const { httpClient } = dependencies;
    const { isEnabled, venueClass } = action;
    const { classId, classTitle, classPricingRule } = venueClass;
    const { venueId } = yield select(selectActiveVenue);
    const updateClassEndpoint = URLS.class.update(classId);

    if (!isEnabled) {
      const classReservationsResponse = yield call(
        [httpClient, httpClient.get],
        URLS.class.reservations(venueId, classId)
      );
      const { reservations } = ReservationsResponse.fromJSON(classReservationsResponse.data);

      const confirmed = yield call(waitForModal, dependencies, 'DisableServiceConfirmationModal', {
        classTitle,
        numberOfReservations: reservations?.length,
      });
      if (!confirmed) return;
    } else if (!classPricingRule) {
      const confirmed = yield call(waitForModal, dependencies, 'EnableServicePricingModal');
      if (confirmed) {
        yield call(openNewTab, LINKS.contact.partnerSuccessHref);
      }
      return;
    }

    yield call([httpClient, httpClient.patch], updateClassEndpoint, { disabled: !isEnabled });

    yield put(createInfoBanner(messages.updateActionSuccess));
    yield call(fetchVenueClassesWithRetailRateRequests, dependencies, {
      venueId,
      onlyActive: false,
      entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
      includeClassPricing: true,
      includePayout: true,
      includeRetailRateRequests: true,
      includeDisabled: true,
    });
  } catch (error) {
    yield put(createErrorBanner(messages.updateActionFailure));
  }
}

export function* handleClassesRouteEnter(client, action) {
  const activeVenue = yield select(selectActiveVenue);
  const venueId = action?.venueId || activeVenue?.venueId;
  const { autosyncStatus, serviceTypes } = activeVenue;
  const featureIsSupported = autosyncStatus && serviceTypes.includes(VENUE_SERVICE_TYPES.APPOINTMENT);

  if (!featureIsSupported) {
    return;
  }

  const includeDisabledClasses = yield select(selectUserHasPermission, PERMISSION.FEATURE_DISABLE_INVENTORY);
  const isPricingProofEnabled = yield call(selectIsServicePricingProofEnabledInSaga);

  yield call(fetchVenueClassesWithRetailRateRequests, client, {
    venueId,
    onlyActive: isPricingProofEnabled ? false : !includeDisabledClasses,
    entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
    includeClassPricing: true,
    includePayout: true,
    includeRetailRateRequests: true,
    includeDisabled: includeDisabledClasses,
  });
}

export function* watchClassListActions(client) {
  yield takeEvery(UPDATE_IS_SERVICE_ENABLED, doUpdateIsServiceEnabled, client);
  yield takeEvery(REFRESH_VENUE_CLASSES, handleClassesRouteEnter, client);
}
